import { useControlledEffect } from "@/features/utils/use-controlled-effect";
import { startAuthentication } from "@simplewebauthn/browser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { AuthAPI } from "../api/auth";
import { userKeys } from "./use-user";
import { useTranslation } from "@/features/utils/i18n";

export function useWebAuthnRegister() {
  return useMutation({
    mutationFn: AuthAPI.webauthnRegister,
  });
}

export function useWebAuthnCompleteRegistration() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AuthAPI.webauthnCompleteRegistration,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.keyRegistered"));
      localStorage.setItem("webauthnUsed", "true");
    },
  });
}

export function useWebAuthnRemoveDevice() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { deviceId: string }>({
    mutationFn: AuthAPI.webauthnRemoveDevice,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.keyRemoved"));
    },
  });
}

export function useWebAuthnChallengeFlow(options: {
  userIdOrEmail?: string; // Optional - if not provided, uses discoverable credentials
  restrictToKeyId?: string; // Restrict to specific key ID
  restrictToKeyName?: string; // Restrict to specific key name (display purposes)
  autoChallenge?: boolean; // Auto-trigger challenge if matching key is found
  onSuccess?: (preSignedToken: string) => void;
  onEndChallenge?: () => void; // Called when auto challenge ends (success or failure)
}) {
  const {
    userIdOrEmail,
    restrictToKeyId,
    restrictToKeyName,
    autoChallenge,
    onSuccess,
    onEndChallenge,
  } = options;
  const { t } = useTranslation();

  // Raw hooks using AuthAPI methods directly
  const challengeRaw = useMutation({
    mutationFn: AuthAPI.webauthnChallenge,
  });
  const verifyRaw = useMutation({
    mutationFn: AuthAPI.webauthnVerify,
  });

  // State
  const [isProcessing, setIsProcessing] = useState(false);

  // Start challenge
  const startChallenge = useCallback(async () => {
    try {
      setIsProcessing(true);
      const result = await challengeRaw.mutateAsync(
        userIdOrEmail ? { userIdOrEmail } : undefined
      );

      if (!result.success) {
        throw new Error(
          result.message || t("security.messages.webauthnStartFailed")
        );
      }

      // Filter credentials if restriction is specified
      let credentialRequestOptions = result.challenge;
      if (restrictToKeyId && credentialRequestOptions.allowCredentials) {
        credentialRequestOptions = {
          ...credentialRequestOptions,
          allowCredentials: credentialRequestOptions.allowCredentials.filter(
            (cred: { id: string }) => cred.id === restrictToKeyId
          ),
        };
      }

      // Trigger WebAuthn browser API using SimpleWebAuthn
      const credential = await startAuthentication({
        optionsJSON: credentialRequestOptions,
      });

      // Verify the credential
      const verifyResult = await verifyRaw.mutateAsync({
        challengeToken: result.challenge.challengeToken,
        credential,
      });

      if (verifyResult.success) {
        toast.success(t("security.messages.keyVerified"));
        localStorage.setItem("webauthnUsed", "true");
        onSuccess?.(verifyResult.preSignedToken);
      } else {
        throw new Error(
          verifyResult.message || t("security.messages.keyVerifyFailed")
        );
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("security.messages.keyAuthFailed");
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsProcessing(false);
      onEndChallenge?.();
    }
  }, [userIdOrEmail, restrictToKeyId, challengeRaw, verifyRaw, onSuccess]);

  // Auto-challenge if enabled
  useControlledEffect(() => {
    if (autoChallenge && userIdOrEmail) {
      console.log("Auto-challenging WebAuthn for user:", userIdOrEmail);
      startChallenge();
    }
  }, []);

  return {
    startChallenge,
    isProcessing: isProcessing || challengeRaw.isPending || verifyRaw.isPending,
    restrictToKeyName,
    didUseWebauthnOnThisDevice: localStorage.getItem("webauthnUsed") === "true",
  };
}
